import { useState, useCallback } from "react";
import { toast } from "sonner";
import { loadFloorPlanFile, loadFromURL, isPDF, openPDF, renderPDFPage } from "@/lib/fileLoader";
import { logError } from "@/lib/log";

const SAMPLE_URL = "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwZmxvb3IlMjBwbGFuJTIwZHJhZnQlMjBibHVlcHJpbnR8ZW58MHx8fHwxNzg5OTY4NTYyfDA&ixlib=rb-4.1.0&q=85";

// Handles file/PDF/sample loading; onLoaded receives { image, width, height }
export function useFloorPlanUpload(onLoaded) {
  const [pdfPick, setPdfPick] = useState(null); // { pdf, fileName }

  const apply = useCallback((loaded, label) => {
    onLoaded(loaded);
    toast.success(`Loaded ${label} (${loaded.width}×${loaded.height})`, { id: "upload" });
  }, [onLoaded]);

  const upload = useCallback(async (file) => {
    try {
      toast.loading("Loading floor plan...", { id: "upload" });
      if (isPDF(file)) {
        const pdf = await openPDF(file);
        if (pdf.numPages > 1) {
          toast.dismiss("upload");
          setPdfPick({ pdf, fileName: file.name });
          return;
        }
        apply(await renderPDFPage(pdf, 1), file.name);
        return;
      }
      apply(await loadFloorPlanFile(file), file.name);
    } catch (e) {
      logError("Floor plan load failed", e);
      toast.error("Failed to load file. Try a JPG, PNG, SVG, or PDF.", { id: "upload" });
    }
  }, [apply]);

  const pickPdfPage = useCallback(async (pageNum) => {
    try {
      toast.loading(`Rendering page ${pageNum}...`, { id: "upload" });
      apply(await renderPDFPage(pdfPick.pdf, pageNum), `${pdfPick.fileName} · page ${pageNum}`);
    } catch (e) {
      logError("PDF page render failed", e);
      toast.error("Failed to render that page", { id: "upload" });
    }
  }, [apply, pdfPick]);

  const loadSample = useCallback(async () => {
    try {
      toast.loading("Loading sample plan...", { id: "upload" });
      apply(await loadFromURL(SAMPLE_URL), "sample plan");
    } catch (e) {
      logError("Sample load failed", e);
      toast.error("Failed to load sample", { id: "upload" });
    }
  }, [apply]);

  return { upload, loadSample, pdfPick, clearPdfPick: () => setPdfPick(null), pickPdfPage };
}
